import React, { useState } from 'react';
import { apiClient } from '../../api/client';

interface SelectedGuest {
  id: string;
  fullName: string;
  tierName: string | null;
  ticketNumber: string | null;
  chapterAffiliation: string | null;
}

interface Props {
  guests: SelectedGuest[];
  onClose: () => void;
  onCreated: (groupName: string) => void;
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1200,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '24px',
    width: '480px',
    maxWidth: '90vw',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#1a6b3c',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.2rem',
    padding: '4px 8px',
    borderRadius: '4px',
    color: '#666',
  },
  fieldLabel: {
    display: 'block',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#444',
    marginBottom: '4px',
  },
  fieldInput: {
    width: '100%',
    padding: '0.55rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '0.88rem',
    boxSizing: 'border-box' as const,
  },
  warningBox: {
    backgroundColor: '#fff8e1',
    border: '1px solid #ffcc02',
    borderRadius: '6px',
    padding: '10px 14px',
    marginTop: '12px',
    fontSize: '0.82rem',
    color: '#7a5e00',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  guestList: {
    marginTop: '14px',
    borderTop: '1px solid #eee',
    paddingTop: '10px',
  },
  guestRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    fontSize: '0.84rem',
    borderBottom: '1px solid #f5f5f5',
  },
  tierBadge: {
    fontSize: '0.72rem',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '10px',
    textTransform: 'uppercase' as const,
  },
  btnRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '18px',
  },
  createBtn: {
    padding: '0.55rem 1.25rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  createBtnDisabled: {
    padding: '0.55rem 1.25rem',
    backgroundColor: '#a5d6a7',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  cancelBtn: {
    padding: '0.55rem 1.25rem',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '8px 12px',
    borderRadius: '4px',
    marginTop: '10px',
    fontSize: '0.82rem',
  },
};

export function CreateFamilyGroupModal({ guests, onClose, onCreated }: Props) {
  const [groupName, setGroupName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Detect mixed tiers
  const tiers = [...new Set(guests.map((g) => g.tierName).filter(Boolean))] as string[];
  const hasMixedTiers = tiers.length > 1;

  const canSubmit = groupName.trim().length > 0 && guests.length >= 2 && !creating;

  const handleCreate = async () => {
    if (!canSubmit) return;
    setCreating(true);
    setError('');
    try {
      await apiClient('/batch-assignment/family-groups/create-with-members', {
        method: 'POST',
        body: {
          name: groupName.trim(),
          guestIds: guests.map((g) => g.id),
          active: true,
        },
      });
      onCreated(groupName.trim());
    } catch (err: any) {
      setError(err?.message || 'Failed to create family group');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h4 style={styles.title}>Create Family Group</h4>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Group Name */}
        <div style={{ marginBottom: '12px' }}>
          <label style={styles.fieldLabel}>Group Name</label>
          <input
            style={styles.fieldInput}
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="e.g. Thomas Family"
            autoFocus
          />
        </div>

        {/* Mixed Tier Warning */}
        {hasMixedTiers && (
          <div style={styles.warningBox}>
            <span style={{ fontSize: '1rem', lineHeight: 1 }}>⚠️</span>
            <div>
              <strong>Mixed tiers detected.</strong> The selected guests have different ticket tiers
              ({tiers.join(', ')}). You can still create the group, but seating may require
              accommodating different tier zones.
            </div>
          </div>
        )}

        {/* Selected Guests List */}
        <div style={styles.guestList}>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#666', marginBottom: '6px' }}>
            {guests.length} Guest{guests.length !== 1 ? 's' : ''} Selected
          </div>
          {guests.map((guest) => (
            <div key={guest.id} style={styles.guestRow}>
              <div>
                <span style={{ fontWeight: 500 }}>{guest.fullName}</span>
                {guest.ticketNumber && (
                  <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#888', fontFamily: 'monospace' }}>
                    {guest.ticketNumber}
                  </span>
                )}
              </div>
              {guest.tierName && (
                <span
                  style={{
                    ...styles.tierBadge,
                    backgroundColor: guest.tierName === 'Patron' ? '#fdf2e9' : '#e8f5e9',
                    color: guest.tierName === 'Patron' ? '#b35900' : '#2e7d32',
                  }}
                >
                  {guest.tierName}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Error */}
        {error && <div style={styles.error}>{error}</div>}

        {/* Actions */}
        <div style={styles.btnRow}>
          <button style={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            style={canSubmit ? styles.createBtn : styles.createBtnDisabled}
            onClick={handleCreate}
            disabled={!canSubmit}
          >
            {creating ? 'Creating...' : 'Create Group'}
          </button>
        </div>
      </div>
    </div>
  );
}
